/**
 * Composition d'une description d'application à partir d'un questionnaire.
 *
 * Décrire une application à un modèle est un exercice que personne ne maîtrise
 * spontanément : on oublie de dire pour qui c'est, ce qu'il advient des données au
 * rechargement, et à quoi doit ressembler un graphique. Le résultat n'est pas
 * mauvais, il est *arbitraire* — le modèle décide à la place de l'auteur, qui ne
 * l'apprend qu'en voyant l'application.
 *
 * Le questionnaire ne restreint pas ce qu'on peut demander : il pose les questions
 * qu'on aurait dû se poser, puis rend un texte que l'auteur relit et complète. Le
 * champ libre reste le dernier mot — ce module fournit un point de départ, pas un
 * formulaire à cases.
 *
 * La composition est une fonction pure, à part de l'interface, parce que c'est
 * elle qui décide de ce que le modèle recevra : elle mérite d'être lisible et
 * testée pour elle-même.
 */

/** Sort réservé aux données saisies dans l'application. */
export type DataPolicy = 'none' | 'persist';

/** Familles de diagrammes proposées, avec ce qu'elles servent à montrer. */
export type ChartKind = 'kpi' | 'bar' | 'line' | 'pie' | 'stacked' | 'gauge' | 'table' | 'scatter';

/** Fonctions courantes, cochées plutôt que décrites. */
export type FeatureKey = 'import' | 'export' | 'search' | 'filters' | 'sort' | 'print' | 'mobile';

/** Ton visuel de l'application. */
export type VisualTone = 'sobre' | 'colore' | 'dense';

export interface GuideAnswers {
  /** À quoi sert l'application. Seule réponse indispensable. */
  purpose: string;
  /** Qui s'en sert. */
  audience: string;
  /** Informations manipulées : les colonnes, les champs, les objets. */
  entities: string;
  dataPolicy: DataPolicy;
  /** Chiffres à suivre, quand l'application en présente. */
  indicators: string;
  charts: ChartKind[];
  features: FeatureKey[];
  tone: VisualTone;
  /** Tout ce que le questionnaire n'a pas demandé. */
  extra: string;
}

export const EMPTY_ANSWERS: GuideAnswers = {
  purpose: '',
  audience: '',
  entities: '',
  dataPolicy: 'none',
  indicators: '',
  charts: [],
  features: [],
  tone: 'sobre',
  extra: '',
};

/**
 * Ce que chaque diagramme montre, et la phrase qui le décrit au modèle.
 *
 * Le libellé est celui qu'on lit à l'écran ; `purpose` explique *quand* le
 * choisir, ce qui est la question que se pose réellement quelqu'un qui hésite
 * entre un camembert et des barres ; `instruction` est ce qui part dans la
 * description, rédigé pour être sans ambiguïté.
 */
export const CHART_CATALOG: ReadonlyArray<{
  kind: ChartKind;
  label: string;
  purpose: string;
  instruction: string;
}> = [
  {
    kind: 'kpi',
    label: 'Chiffres clés',
    purpose: 'Quelques nombres importants, en grand, tout en haut.',
    instruction:
      'une rangée de cartes de chiffres clés en haut de page, chacune avec son intitulé, ' +
      'sa valeur en grand et, si la comparaison a du sens, son évolution',
  },
  {
    kind: 'bar',
    label: 'Barres',
    purpose: 'Comparer des catégories entre elles.',
    instruction: 'un diagramme en barres pour comparer les catégories entre elles',
  },
  {
    kind: 'line',
    label: 'Courbe',
    purpose: 'Suivre une évolution dans le temps.',
    instruction: 'une courbe pour suivre l’évolution dans le temps',
  },
  {
    kind: 'pie',
    label: 'Camembert',
    purpose: 'Montrer la répartition d’un total en parts.',
    instruction:
      'un diagramme circulaire pour montrer la répartition en parts d’un total, avec le ' +
      'pourcentage de chaque part',
  },
  {
    kind: 'stacked',
    label: 'Barres empilées',
    purpose: 'Comparer des totaux tout en montrant leur composition.',
    instruction:
      'un diagramme en barres empilées pour comparer des totaux tout en montrant leur composition',
  },
  {
    kind: 'gauge',
    label: 'Jauge',
    purpose: 'Situer une valeur par rapport à un objectif.',
    instruction: 'une jauge de progression situant la valeur courante par rapport à son objectif',
  },
  {
    kind: 'table',
    label: 'Tableau',
    purpose: 'Voir les données ligne par ligne.',
    instruction: 'un tableau des données, lisible ligne par ligne',
  },
  {
    kind: 'scatter',
    label: 'Nuage de points',
    purpose: 'Chercher un lien entre deux grandeurs.',
    instruction: 'un nuage de points pour faire apparaître le lien entre deux grandeurs',
  },
];

/** Fonctions proposées, et la phrase correspondante. */
export const FEATURE_CATALOG: ReadonlyArray<{
  key: FeatureKey;
  label: string;
  hint: string;
  instruction: string;
}> = [
  {
    key: 'import',
    label: 'Importer un fichier',
    hint: 'CSV ou Excel, depuis le poste de l’utilisateur',
    instruction:
      'un import de fichier CSV ou Excel, avec un aperçu des lignes lues et le nombre de ' +
      'lignes ignorées en cas de problème',
  },
  {
    key: 'export',
    label: 'Exporter les données',
    hint: 'un bouton qui télécharge un CSV',
    instruction: 'un export CSV de ce qui est affiché, filtres compris',
  },
  {
    key: 'search',
    label: 'Rechercher',
    hint: 'un champ qui filtre au fil de la saisie',
    instruction: 'une recherche qui filtre la liste au fil de la saisie',
  },
  {
    key: 'filters',
    label: 'Filtrer',
    hint: 'par état, par date, par catégorie',
    instruction:
      'des filtres sur les principaux champs, cumulables, avec un bouton de remise à zéro',
  },
  {
    key: 'sort',
    label: 'Trier',
    hint: 'en cliquant sur l’en-tête d’une colonne',
    instruction: 'un tri par clic sur l’en-tête de chaque colonne, dans les deux sens',
  },
  {
    key: 'print',
    label: 'Imprimer proprement',
    hint: 'une mise en page correcte à l’impression',
    instruction:
      'une feuille de style d’impression : pas de menus ni de boutons sur le papier, ' +
      'tableaux non coupés au milieu d’une ligne',
  },
  {
    key: 'mobile',
    label: 'Utilisable sur téléphone',
    hint: 'la mise en page s’adapte aux petits écrans',
    instruction:
      'une mise en page utilisable sur téléphone : une seule colonne en dessous de 640 px, ' +
      'cibles tactiles confortables, tableaux défilables horizontalement',
  },
];

const TONE_INSTRUCTION: Record<VisualTone, string> = {
  sobre:
    'Présentation sobre et professionnelle : peu de couleurs, beaucoup de blanc, une seule ' +
    'couleur d’accent.',
  colore:
    'Présentation colorée et vivante : couleurs franches pour distinguer les catégories, ' +
    'sans nuire à la lisibilité du texte.',
  dense:
    'Présentation dense : le maximum d’information à l’écran sans défilement, textes compacts, ' +
    'marges réduites.',
};

/** Coupe les espaces et les lignes vides d'un champ libre. */
function propre(valeur: string): string {
  return valeur
    .split('\n')
    .map((ligne) => ligne.trim())
    .filter((ligne) => ligne.length > 0)
    .join('\n')
    .trim();
}

/** Une réponse suffit-elle à composer une description exploitable ? */
export function isGuideUsable(answers: GuideAnswers): boolean {
  // L'objet de l'application est le seul point sur lequel personne ne peut décider
  // à la place de l'auteur. Tout le reste admet un défaut raisonnable.
  return propre(answers.purpose).length >= 15;
}

/**
 * Compose la description envoyée au modèle.
 *
 * Le texte est structuré par intertitres plutôt que rédigé d'un trait : le modèle
 * suit mieux une liste de contraintes qu'un paragraphe, et l'auteur relit mieux ce
 * qui est séparé. Aucune section vide n'est produite — une rubrique « Diagrammes »
 * sans diagramme se lirait comme une demande, et le modèle en inventerait.
 */
export function buildGuidedDescription(answers: GuideAnswers): string {
  const blocs: string[] = [];

  blocs.push(`Application souhaitée : ${propre(answers.purpose)}`);

  const audience = propre(answers.audience);
  if (audience) blocs.push(`Utilisateurs : ${audience}`);

  const entities = propre(answers.entities);
  if (entities) {
    blocs.push(`Informations manipulées :\n${liste(entities.split('\n'))}`);
  }

  /**
   * La persistance est annoncée dans les termes qui la déclenchent, et sa
   * contrepartie est écrite noir sur blanc. C'est de cette phrase que dépend
   * l'attribution d'une base de données, et l'auteur doit lire ce qu'il demande.
   */
  blocs.push(
    answers.dataPolicy === 'persist'
      ? 'Conservation des données : les données saisies ou importées doivent être conservées ' +
          'd’une visite à l’autre, et rester disponibles pour tous les utilisateurs de ' +
          'l’application.'
      : 'Conservation des données : aucune. Les données ne survivent pas au rechargement de ' +
          'la page, ce qui est volontaire.',
  );

  const indicators = propre(answers.indicators);
  const charts = CHART_CATALOG.filter((entree) => answers.charts.includes(entree.kind));
  if (indicators || charts.length > 0) {
    const lignes: string[] = [];
    if (indicators) lignes.push(`Chiffres à suivre : ${indicators}`);
    if (charts.length > 0) {
      lignes.push(`Visualisations attendues :\n${liste(charts.map((c) => c.instruction))}`);
      lignes.push(
        'Chaque graphique porte un titre, des axes légendés avec leur unité, et un message ' +
          'explicite quand il n’y a aucune donnée à montrer — jamais un cadre vide.',
      );
    }
    blocs.push(lignes.join('\n'));
  }

  const features = FEATURE_CATALOG.filter((entree) => answers.features.includes(entree.key));
  if (features.length > 0) {
    blocs.push(`Fonctions attendues :\n${liste(features.map((f) => f.instruction))}`);
  }

  blocs.push(TONE_INSTRUCTION[answers.tone]);

  const extra = propre(answers.extra);
  if (extra) blocs.push(`Précisions de l’auteur :\n${extra}`);

  return blocs.join('\n\n');
}

/** Liste à puces, une entrée par ligne. */
function liste(entrees: readonly string[]): string {
  return entrees.map((entree) => `- ${entree}`).join('\n');
}
